<template>
  <ul class="weekplan box text-dark" :class="{'weekplan--editable': !isReadOnly}" ref="d_weekplan"
      v-if="!isInPrintMode || eventsFiltered.length > 0">
    <div class="weekplan-placeholder box" v-if="!isInViewport" :style="placeholderHeight">
      <h2>{{ i18n[locale].weekplan }}</h2>
      <Loader v-if="isWeekplanHasLesson" bg-color="#fff"></Loader>
    </div>
    <template v-if="isInViewport">
      <div class="deip-holder" @click.prevent ref="d_eip">
        <EIP v-if="activeEvent"
             :activeEIP="activeEIP"
             :lesson="activeEvent"
             :noteType="activeNoteType"
             :isUploadFile="false"
             :isClosingWithoutSaving="isClosingWithoutSaving"
             :isSaving="isSavingEIP"
             @changed="setIsEditorChangedWithoutSave"
             @close="closeEip">
        </EIP>
      </div>
      <h2 class="marg-1-bottom">{{ i18n[locale].weekplan }}</h2>
      <div class="week_note" v-if="weekNote !== undefined && calendar && calendar.type === 'klass'">
        <span v-html="weekNote" v-if="isReadOnly"></span>
        <div class="weekplan--grid__note"
             v-if="!isReadOnly"
             :id="calendar.id + '_' + 'weeknote'"
             @click.prevent="setActiveEip(null, 'weeknote', calendar.id + '_' + 'weeknote', $event)"
             :class="[{'weekplan--grid__note--open': activeEventId === calendar.id + '_' + 'weeknote'}, {'weekplan--grid__note--empty': weekNote.length < 1}]">
          <span v-if="activeEventId !== calendar.id + '_' + 'weeknote'" v-html="weekNote"></span>
        </div>
      </div>
      <p v-if="!isWeekplanHasLesson" class="text-explainer">{{ i18n[locale].no_events_this_week }}</p>
      <li class="weekplan__day-change"
          v-for="(day, key) in eventsWithinDays" v-if="day.lessons.length !== 0" :key="day.date">
        <h3>{{ filterLocaleDate(day.date) }}</h3>
        <div class="weekplan__grid day-change__headline">
          <p class="weekplan__grid__title">
            <template v-if="calendar.type !== 'other'"></template>
          </p>
          <p>
            <template v-if="calendar.type !== 'other'">{{ i18n[locale].learning_goal }}</template>
            <template v-if="calendar.type === 'other'">{{ i18n[locale].meeting_purpose }}</template>
          </p>
          <p>
            <template v-if="calendar.type !== 'other'">{{ i18n[locale].content }}</template>
            <template v-if="calendar.type === 'other'">{{ i18n[locale].agenda }}</template>
            <p>
              <template v-if="calendar.type !== 'other'">{{ i18n[locale].pupil_preparation }}</template>
              <template v-if="calendar.type === 'other'">{{ i18n[locale].decisions_and_resume }}</template>
            </p>
            <p>
              <template v-if="calendar.type !== 'other'">{{ i18n[locale].teacher_notes }}</template>
            </p>
            <template v-if="calendar.type === 'other'">{{ i18n[locale].internal_notes }}</template>
        </div>
        <ul>
          <li v-for="event in day.lessons" :key="event.event_id"
              class="box week-plan__lesson__note weekplan__grid" :class="{'lesson__notes--readonly': isReadOnly}"
              :style="{ borderColor: event.color }">
            <div class="weekplan__grid__title">
              <span class="bold">{{ event.title }}</span><span v-if="event.group">, {{ event.group }}</span>
              <br>
              <span>{{ event.start | timeStamp }} - {{ event.end | timeStamp }}<br></span>
            </div>
            <div class="weekplan--grid__note"
                 :id="event.event_id + '_' + 'learning_goal'"
                 @click.prevent="setActiveEip(event, 'learning_goal', event.event_id + '_' + 'learning_goal', $event)"
                 :class="[{'weekplan--grid__note--open': activeEventId === event.event_id + '_' + 'learning_goal'}, {'weekplan--grid__note--empty': event.learning_goal.length < 1}]">
              <span v-if="activeEventId !== event.event_id + '_' + 'learning_goal'" v-html="event.learning_goal"></span>
            </div>
            <div class="weekplan--grid__note"
                 :id="event.event_id + '_' + 'description'"
                 @click.prevent="setActiveEip(event, 'description', event.event_id + '_' + 'description', $event)"
                 :class="[{'weekplan--grid__note--open': activeEventId === event.event_id + '_' + 'description'}, {'weekplan--grid__note--empty': event.description.length < 1}]">
              <span v-if="activeEventId !== event.event_id + '_' + 'description'" v-html="event.description"></span>
            </div>
            <div class="weekplan--grid__note"
                 :id="event.event_id + '_' + 'notes_for_students'"
                 @click.prevent="setActiveEip(event, 'notes_for_students', event.event_id + '_' + 'notes_for_students', $event)"
                 :class="[{'weekplan--grid__note--open': activeEventId === event.event_id + '_' + 'notes_for_students'}, {'weekplan--grid__note--empty': event.notes_for_students.length < 1}]">
              <span v-if="activeEventId !== event.event_id + '_' + 'notes_for_students'"
                    v-html="event.notes_for_students"></span>
            </div>
            <div class="weekplan--grid__note"
                 :id="event.event_id + '_' + 'teacher_notes'"
                 @click.prevent="setActiveEip(event, 'teacher_notes', event.event_id + '_' + 'teacher_notes', $event)"
                 :class="[{'weekplan--grid__note--open': activeEventId === event.event_id + '_' + 'teacher_notes'}, {'weekplan--grid__note--empty': event.teacher_notes.length < 1}]">
              <span v-if="activeEventId !== event.event_id + '_' + 'teacher_notes'" v-html="event.teacher_notes"></span>
            </div>
          </li>
        </ul>
      </li>
    </template>
  </ul>
</template>

<script>
import moment from 'moment'
import Loader from '../common/Loader'
import EIP from './DEIP'
import docendo from '../../docendo'
import eventbus from '../../eventbus'

const I18N = {
  da: {
    weekplan: 'Ugeplan',
    no_events_this_week: 'Ingen lektioner eller noter i ugen',
    learning_goal: 'Læringsmål',
    meeting_purpose: 'Formål med mødet',
    content: 'Indhold',
    agenda: 'Agenda',
    pupil_preparation: 'Elevforberedelse',
    decisions_and_resume: 'Beslutninger og resumé',
    teacher_notes: 'Vikarnoter / Interne noter',
    internal_notes: 'Noter'
  },
  en: {
    weekplan: 'Weekplan',
    no_events_this_week: 'No lessons or notes this week',
    learning_goal: 'Learning goal',
    meeting_purpose: 'Purpose of the meeting',
    content: 'Content',
    agenda: 'Agenda',
    pupil_preparation: 'Homework',
    decisions_and_resume: 'Decisions and summary',
    internal_notes: 'Notes',
    teacher_notes: 'Teachers notes'
  },
  nb: {
    weekplan: 'Ukeplan',
    no_events_this_week: 'Ingen leksjoner eller notater denne uken',
    learning_goal: 'Læringsmål',
    meeting_purpose: 'Formål med møtet',
    content: 'Innhold',
    agenda: 'Agenda',
    pupil_preparation: 'Elevforberedelse',
    decisions_and_resume: 'Beslutninger og resymé',
    teacher_notes: 'Vikarnotater',
    internal_notes: 'Notater'
  }
}

export default {
  props: {
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isI18N: {
      type: Boolean,
      default: false
    },
    isInPrintMode: {
      type: Boolean,
      default: false
    }
  },
  components: {Loader, EIP},
  data () {
    return {
      isInViewport: false,
      isWeekplanHasLesson: false,
      activeEIP: null,
      activeEvent: null,
      activeNoteType: '',
      activeText: '',
      isEditorChangedWithoutSave: false,
      isClosingWithoutSaving: false,
      weekNote: '',
      weeknoteId: null,
      weekNoteState: '',
      isSavingEIP: false
    }
  },
  computed: {
    i18n () {
      return I18N
    },
    placeholderHeight () {
      let boxHeight = 27 + 12
      let dayHeight = 24 + 12 + 22 + 34
      let eventHeight = 68 + 12
      let days = 0
      let events = 0
      this.eventsWithinDays.forEach(day => {
        if (day.lessons.length > 0) {
          days++
          events += day.lessons.length
        }
      })
      if (days < 1) {
        return 'height: 61px'
      }
      let height = boxHeight + (days * dayHeight) + (events * eventHeight)
      return 'height:' + height + 'px'
    },
    locale () {
      return this.$store.getters.locale
    },
    isEventsLoading () {
      return this.$store.state.calendar.isEventsLoading
    },
    calendar () {
      return this.$store.state.calendar.calendar
    },
    date () {
      return this.$store.state.calendar.date
    },
    events () {
      return this.$store.getters.activeFullCalendarEvents
    },
    eventsFiltered () {
      return this.isInPrintMode || this.isReadOnly ? this.events.filter(event => event.has_notes) : this.events
    },
    eventsInHash () {
      return this.$store.getters.activeFullCalendarEventsInHash
    },
    eventsWithinDays () {
      this.isWeekplanHasLesson = false
      let allDates = []
      for (let i = 1; i < 8; i++) {
        let date = moment(this.date).isoWeekday(i).format('YYYY-MM-DD')
        let arr = this.filterLesson(i).concat()
        arr.sort(function compare (a, b) {
          var dateA = new Date(a.start)
          var dateB = new Date(b.start)
          return dateA - dateB
        })
        if (arr.length > 0) {
          this.isWeekplanHasLesson = true
        }
        allDates.push({date, lessons: arr})
      }
      return allDates
    },
    activeEventId () {
      return !this.activeEvent ? '' : this.activeEvent.event_id + '_' + this.activeNoteType
    }
  },
  watch: {
    date () {
      this.cleanUp()
      // hide weekplan if user has a open note and is changing date
      this.isInViewport = false
      this.getWeekNote()
    },
    isInPrintMode (val) {
      if (val) {
        this.isInViewport = true
      }
    },
    calendar () {
      this.getWeekNote()
    }
  },
  methods: {
    createPopper (divId) {
      let el = document.getElementById(divId)
      let source = this.$refs.d_eip
      el.appendChild(source)
    },
    closeEip (obj) {
      if (!obj) {
        this.activeEvent = null
        return
      }
      if (obj.noteType === 'weeknote') {
        this.updateWeekNote(obj.text)
        return
      }
      this.isSavingEIP = true
      docendo.updateEvent(obj.lesson.school_id, obj.lesson.event_id, {[obj.noteType]: obj.text}, payload => {
        let updateObj = {
          noteType: obj.noteType,
          payload: payload
        }
        this.$store.commit('updateActiveCalendarEventNote', updateObj)
        this.activeEvent = null
        this.isSavingEIP = false
        eventbus.toast = {text: this.$t('vue2.common.saved'), 'success': true}
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
    },
    setActiveEip (event, noteType, divId, evt) {
      if (this.isReadOnly) {
        return
      }
      if (this.activeEvent && divId === this.activeEvent.event_id + '_' + this.activeNoteType) {
        return
      }
      if (evt.target.tagName.toLowerCase() === 'a' && evt.target.href.length > 0) {
        evt.stopPropagation()
        window.open(evt.target.href, '_blank', 'noopener,noreferrer')
        return
      }
      if (this.isEditorChangedWithoutSave) {
        this.isClosingWithoutSaving = true
        return
      }
      this.isClosingWithoutSaving = false
      this.createPopper(divId)
      this.$nextTick(_ => {
        if (noteType === 'weeknote') {
          const activeEvent = {
            weeknote: this.weekNote,
            event_id: this.calendar.id
          }
          this.activeEvent = activeEvent
          this.activeNoteType = noteType
          this.activeText = this.weekNote
          this.activeEIP = this.calendar.id + '_' + 'weeknote'
        } else {
          this.activeEvent = event
          this.activeNoteType = noteType
          this.activeText = this.activeEvent[this.activeNoteType]
          this.activeEIP = event.event_id + '_' + noteType
        }
      })
    },
    setIsEditorChangedWithoutSave (boolean) {
      this.isEditorChangedWithoutSave = boolean
    },
    filterLocaleDate (mom) {
      // NN hack
      if (this.locale === 'nn') {
        return moment(mom).format('dddd DD. MMMM')
      }
      try {
        return moment(mom)._d.toLocaleDateString(this.locale, {weekday: 'long', month: 'long', day: '2-digit'})
      } catch (e) {
        return moment(mom).format('LL')
      }
    },
    getEvent (eventId) {
      return this.eventsInHash[eventId]
    },
    filterLesson (dayNum) {
      let allowedEventTypes = ['subject', 'supplemental', 'exercise', 'resource', 'frame', 'optional', 'person_resource', 'person_resource_visible', 'other_tasks', 'other_stuff']
      return this.eventsFiltered.filter(lesson => {
        if (allowedEventTypes.indexOf(lesson.event_type) > -1) {
          let lessonDate = moment(lesson.start).format('YYYY-MM-DD')
          let day = moment(this.date).isoWeekday(dayNum).format('YYYY-MM-DD')
          return moment(lessonDate).isSame(day)
        }
      })
    },
    cleanUp () {
      this.activeEvent = null
      this.activeNoteType = ''
      this.activeText = ''
      this.activeEIP = null
    },
    toogleWeekplan (evt) {
      // If a notefield is open then don't tear down the weekplan
      if (this.activeEIP) {
        return
      }
      this.isInViewport = evt[0].isIntersecting
      if (!this.isInViewport) {
        this.cleanUp()
      }
    },
    scrollTrigger () {
      let observer = new window.IntersectionObserver(this.toogleWeekplan)
      if (!this.isInPrintMode) {
        observer.observe(this.$refs.d_weekplan)
      }
    },
    getWeekNote () {
      if (!this.calendar || (this.calendar && this.calendar.type !== 'klass')) {
        return
      }
      const isoWeek = moment(this.date).isoWeek().toString().length < 2 ? '0' + moment(this.date).isoWeek() : moment(this.date).isoWeek()
      const week = moment(this.date).format('YYYY') + 'W' + isoWeek
      docendo.weeknote(this.calendar.id, week, data => {
        this.weekNote = data.note ? data.note : ''
        // this.isWeekNoteLoaded = true
        this.weeknoteId = data.note ? data.id : null
      })
    },
    updateWeekNote (note) {
      this.isSavingEIP = true
      const isoWeek = moment(this.date).isoWeek().toString().length < 2 ? '0' + moment(this.date).isoWeek() : moment(this.date).isoWeek()
      const week = moment(this.date).format('YYYY') + 'W' + isoWeek
      this.weekNoteState = 'saving'
      docendo.weeknoteUpdate(this.calendar.id, week, note, success => {
        this.weekNoteState = 'saved'
        if (success.hasOwnProperty('note')) {
          this.weekNote = success.note
          this.weeknoteId = success.id
        }
        this.activeEvent = null
        this.isSavingEIP = false
        eventbus.toast = {text: this.$t('vue2.common.saved'), 'success': true}
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.toast.something_went_wrong'), 'success': false}
      })
    }
  },
  mounted () {
    this.scrollTrigger()
    this.getWeekNote()
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.weekplan {
  box-shadow: 0 1px 2px rgb(0 0 0 / 7%);
  background: #fff;
  padding: 1rem;
  overflow: hidden;
  border-radius: 0.25rem;
  margin-top: 1rem;
}

.weekplan__day-change {
  background: #f6f7f8;
  padding: 1rem;
  margin-bottom: 1rem;
  content-visibility: auto;
  contain-intrinsic-size: 1px 480px;
}

.weekplan__grid {
  display: flex;
  align-items: flex-start;
}

.weekplan__grid > div,
.weekplan__grid > p {
  flex: 1;
}

.weekplan__grid > .weekplan__grid__title {
  padding: 0 10px;
}

.weekplan--grid__note {
  padding: 10px;
}

.weekplan--grid__note > span > p {
  padding: 1rem;
}

.weekplan__grid.day-change__headline > p {
  font-weight: bold;
  padding: 0 10px;
}

.day-change__headline {
  padding: 0.5rem 23px;
}

.weekplan--editable .weekplan--grid__note {
  cursor: pointer;
}

.weekplan--editable .weekplan--grid__note:hover:not(.weekplan--grid__note--open) {
  background-color: lightgoldenrodyellow;
}

.weekplan--editable .weekplan--grid__note--empty:not(.weekplan--grid__note--open) {
  position: relative;
  min-width: 20px;
  min-height: 20px;
  cursor: pointer;
}

.weekplan--editable .weekplan--grid__note--empty:not(.weekplan--grid__note--open):before {
  content: "\E90E";
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  -webkit-font-feature-settings: normal;
  font-feature-settings: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.week_note {
  font-size: 13px;
  background: #f6f7f8;
  border-left: 3px solid #f6f7f8;
  border-radius: 3px;
  overflow: visible;
  padding: 10px 20px;
  margin-bottom: 10px;
}

@media print {
  .week-plan__lesson__note {
    break-inside: avoid;
  }
}

</style>



// WEBPACK FOOTER //
// DWeekplan.vue?4b4380d3